import React, { useEffect, useState, useMemo, useContext } from "react";
import axios from "axios";
import { useOutletContext, useNavigate } from "react-router-dom"; 
import { FaCheck, FaEye, FaEdit, FaTrash, FaSearch, FaFilePdf, FaWhatsapp, FaDownload } from "react-icons/fa";
import { UserContext } from "../../context/UserContext";
import "../../styles/associadosCircuito.css";

export default function AssociadosCircuito() {
  const navigate = useNavigate(); 
  const [associados, setAssociados] = useState([]);
  
  const { circuito, slugAtivo } = useOutletContext();
  const circuitoId = circuito?._id;

  // Prioriza o token do contexto React (funciona em domínios exclusivos
  // onde o localStorage pode estar vazio). Cai no localStorage como fallback.
  const { token: tokenCtx } = useContext(UserContext);
  const getToken = () => tokenCtx || localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [loadingDownload, setLoadingDownload] = useState(false);
  
  const [enviandoWpp, setEnviandoWpp] = useState(null);
  const [baixandoPdf, setBaixandoPdf] = useState(null); 
  
  const [associadoSelecionado, setAssociadoSelecionado] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // 💡 ESTADOS DE FILTRO ATUALIZADOS
  const [buscaNomeEquipe, setBuscaNomeEquipe] = useState("");
  const [buscaCpf, setBuscaCpf] = useState(""); // Novo estado para buscar CPF
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  const isCustomDomain = window.location.hostname !== "ticketdovaqueiro.com" && window.location.hostname !== "www.ticketdovaqueiro.com" && window.location.hostname !== "localhost";
  const basePath = isCustomDomain ? "/painel" : `/painel-circuito/${slugAtivo}`;

  useEffect(() => {
    if (circuitoId) buscarAssociados();
  }, [circuitoId]);

  // 💡 Reseta a página sempre que qualquer filtro mudar
  useEffect(() => {
    setPaginaAtual(1);
  }, [buscaNomeEquipe, buscaCpf, filtroCategoria, filtroStatus]);

  async function buscarAssociados() {
    try {
      const token = getToken();
      const { data } = await axios.get(
        `https://api.ticketdovaqueiro.com/api/circuitos/${circuitoId}/inscricoes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssociados(data);
    } catch (error) {
      console.error("Erro ao buscar inscrições:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleDownloadPDF = async () => {
    if (!filtroCategoria) return; 
    
    setLoadingDownload(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `https://api.ticketdovaqueiro.com/api/circuitos/${circuitoId}/relatorio-associados`,
        {
          params: { categoria: filtroCategoria },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' 
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Relatorio-Associados-${filtroCategoria}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Erro ao baixar PDF", error);
      alert("Erro ao gerar o relatório PDF. Tente novamente.");
    } finally {
      setLoadingDownload(false);
    }
  };

  const baixarComprovanteIndividual = async (pedidoId, nomeVaqueiro) => {
    if (!pedidoId) return alert("Pedido não encontrado para esta associação.");
    setBaixandoPdf(pedidoId);
    try {
      const res = await axios.get(`https://api.ticketdovaqueiro.com/api/circuitos/pedidos/${pedidoId}/comprovante`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comprovante-${circuito?.nome?.replace(/\s+/g, '') || 'Circuito'}-${nomeVaqueiro?.replace(/\s+/g, '') || 'Vaqueiro'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Erro ao baixar comprovante individual", error);
      alert("Erro ao baixar o comprovante. Tente novamente.");
    } finally {
      setBaixandoPdf(null);
    }
  };

  async function enviarComprovanteWhatsApp(id) {
    setEnviandoWpp(id);
    try {
      const token = getToken();
      await axios.post(
        `https://api.ticketdovaqueiro.com/api/circuitos/${circuitoId}/inscricoes/${id}/enviar-comprovante`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Comprovante enviado com sucesso para o WhatsApp do vaqueiro!");
    } catch (error) {
      console.error("Erro ao enviar wpp:", error);
      alert(error.response?.data?.erro || "Erro ao enviar comprovante pelo WhatsApp.");
    } finally {
      setEnviandoWpp(null);
    }
  }

  function abrirModal(associado) { setAssociadoSelecionado(associado); setModalOpen(true); }
  function fecharModal() { setModalOpen(false); setAssociadoSelecionado(null); }

  async function cancelarAssociacao(id) {
    if (!window.confirm("Deseja realmente cancelar essa associação?")) return;
    try {
      const token = getToken();
      await axios.delete(`https://api.ticketdovaqueiro.com/api/circuitos/${circuitoId}/inscricoes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      buscarAssociados();
    } catch (error) { alert("Erro ao cancelar associação."); }
  }

  async function aprovarPagamento(id) {
    if (!window.confirm("Deseja aprovar MANUALMENTE o pagamento desta associação?")) return;
    try {
      const token = getToken();
      await axios.patch(`https://api.ticketdovaqueiro.com/api/circuitos/${circuitoId}/inscricoes/${id}/aprovar`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert("Pagamento aprovado com sucesso!");
      buscarAssociados();
    } catch (error) { alert("Erro ao aprovar pagamento."); }
  }

  const removerAcentos = (str) => {
    if (!str) return "";
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  };

  const categoriasUnicas = useMemo(() => {
    const cats = associados.map((a) => a.categoria).filter(Boolean);
    return [...new Set(cats)].sort();
  }, [associados]);

  // 💡 LÓGICA DE FILTRAGEM ATUALIZADA
  const associadosFiltrados = useMemo(() => {
    return associados.filter((assoc) => {
      // 1. Filtro por Nome ou Equipe
      const termoTexto = removerAcentos(buscaNomeEquipe);
      const matchTexto = removerAcentos(assoc.nomeVaqueiro).includes(termoTexto) || removerAcentos(assoc.equipe).includes(termoTexto);
      
      // 2. Filtro por CPF (removendo todos os caracteres que não sejam números)
      const termoCpf = buscaCpf.replace(/\D/g, ""); // Mantém só números da busca
      const cpfNoBanco = assoc.cpfVaqueiro || assoc.userId?.cpf || ""; // Busca das duas origens possíveis
      const cpfLimpoNoBanco = cpfNoBanco.replace(/\D/g, ""); // Limpa o CPF que vem do banco
      const matchCpf = termoCpf ? cpfLimpoNoBanco.includes(termoCpf) : true;

      // 3. Filtros Dropdown
      const matchCategoria = filtroCategoria ? assoc.categoria === filtroCategoria : true;
      const matchStatus = filtroStatus ? assoc.statusPagamento === filtroStatus : true;
      
      return matchTexto && matchCpf && matchCategoria && matchStatus;
    });
  }, [associados, buscaNomeEquipe, buscaCpf, filtroCategoria, filtroStatus]);

  const associadosOrdenados = useMemo(() => {
    return [...associadosFiltrados].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [associadosFiltrados]);

  const indexUltimoItem = paginaAtual * itensPorPagina;
  const indexPrimeiroItem = indexUltimoItem - itensPorPagina;
  const associadosPaginados = associadosOrdenados.slice(indexPrimeiroItem, indexUltimoItem);
  const totalPaginas = Math.ceil(associadosOrdenados.length / itensPorPagina);

  return (
    <div className="pc-associados">
      
      <div className="pc-associados-header">
        <h2 className="pc-title">Associados do Circuito</h2>
        
        <button 
          onClick={handleDownloadPDF} 
          disabled={!filtroCategoria || loadingDownload}
          className="btn-download-pdf"
          title={!filtroCategoria ? "Selecione uma Categoria no filtro abaixo para liberar o PDF" : "Baixar Relatório em PDF"}
        >
          <FaFilePdf size={18} /> 
          {loadingDownload ? "Gerando..." : !filtroCategoria ? "Selecione a Categoria para Gerar PDF" : `Gerar PDF: ${filtroCategoria}`}
        </button>
      </div>

      <div className="pc-filters-container">
        <div className="pc-filter-group" style={{ flex: 1.5 }}>
          <label>Buscar Vaqueiro ou Equipe</label>
          <div className="pc-filter-input-wrapper">
            <FaSearch />
            <input 
              type="text" 
              className="pc-filter-input"
              placeholder="Digite o nome..." 
              value={buscaNomeEquipe}
              onChange={(e) => setBuscaNomeEquipe(e.target.value)}
            />
          </div>
        </div>

        {/* 💡 NOVO CAMPO DE BUSCA DE CPF */}
        <div className="pc-filter-group" style={{ flex: 1 }}>
          <label>Buscar por CPF</label>
          <div className="pc-filter-input-wrapper">
            <FaSearch />
            <input 
              type="text" 
              className="pc-filter-input"
              placeholder="Digite o CPF..." 
              value={buscaCpf}
              onChange={(e) => setBuscaCpf(e.target.value)}
            />
          </div>
        </div>

        <div className="pc-filter-group">
          <label>Categoria</label>
          <select 
            className="pc-filter-select"
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
          >
            <option value="">Todas as categorias</option>
            {categoriasUnicas.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="pc-filter-group">
          <label>Status</label>
          <select 
            className="pc-filter-select"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="pago">Pago</option>
            <option value="pendente">Pendente</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      </div>

      <div className="pc-card" style={{ padding: 0, overflow: "hidden" }}>
        
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table className="pc-table" style={{ minWidth: "1200px", margin: 0 }}>
            <thead>
              <tr>
                <th>Nome do Vaqueiro</th>
                <th>CPF</th> {/* 💡 Adicionado na tabela para facilitar a visualização da busca */}
                <th>WhatsApp</th>
                <th>Categoria</th>
                <th>Equipe</th>
                <th>Status Pagamento</th>
                <th>Data Associação</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>Carregando...</td></tr>
              ) : associadosPaginados.length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.5)" }}>Nenhum associado encontrado para os filtros aplicados.</td></tr>
              ) : (
                associadosPaginados.map((assoc) => (
                    <tr key={assoc._id}>
                      <td>{assoc.nomeVaqueiro}</td>
                      {/* 💡 Exibindo o CPF formatado ou cru */}
                      <td>{assoc.cpfVaqueiro || assoc.userId?.cpf || "N/A"}</td>
                      <td>{assoc.userId?.whatsapp || "N/A"}</td>
                      <td>{assoc.categoria}</td>
                      <td>{assoc.equipe}</td>
                      <td><span className={`status ${assoc.statusPagamento}`}>{assoc.statusPagamento}</span></td>
                      <td>{new Date(assoc.createdAt).toLocaleDateString()}</td>
                      <td className="acoes" style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        
                        {assoc.statusPagamento === "pago" && (
                          <button 
                            title="Baixar Comprovante em PDF" 
                            onClick={() => baixarComprovanteIndividual(assoc.pedidoId?._id || assoc.pedidoId, assoc.nomeVaqueiro)}
                            disabled={baixandoPdf === (assoc.pedidoId?._id || assoc.pedidoId)}
                            style={{
                              background: "rgba(239, 68, 68, 0.15)", color: "#ef4444", border: "none", 
                              padding: "8px", borderRadius: "6px", cursor: baixandoPdf === (assoc.pedidoId?._id || assoc.pedidoId) ? "wait" : "pointer", 
                              transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: baixandoPdf === (assoc.pedidoId?._id || assoc.pedidoId) ? 0.5 : 1
                            }}
                          >
                            <FaDownload size={15} />
                          </button>
                        )}

                        {assoc.statusPagamento === "pago" && (
                          <button 
                            title="Enviar Comprovante no WhatsApp" 
                            onClick={() => enviarComprovanteWhatsApp(assoc._id)}
                            disabled={enviandoWpp === assoc._id}
                            style={{
                              background: "rgba(37, 211, 102, 0.15)", color: "#25D366", border: "none", 
                              padding: "8px", borderRadius: "6px", cursor: enviandoWpp === assoc._id ? "wait" : "pointer", 
                              transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: enviandoWpp === assoc._id ? 0.5 : 1
                            }}
                          >
                            <FaWhatsapp size={15} />
                          </button>
                        )}

                        {assoc.statusPagamento !== "pago" && (
                          <button className="btn-aprovar" title="Aprovar Pagamento Manual" onClick={() => aprovarPagamento(assoc._id)}>
                            <FaCheck />
                          </button>
                        )}

                        <button className="btn-visualizar" title="Visualizar Detalhes" onClick={() => abrirModal(assoc)}>
                          <FaEye />
                        </button>

                        <button className="btn-editar" title="Editar" onClick={() => navigate(`${basePath}/associados/editar/${assoc._id}`)}>
                          <FaEdit />
                        </button>

                        <button className="btn-cancelar" title="Excluir" onClick={() => cancelarAssociacao(assoc._id)}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>

        {/* CONTROLES DE PAGINAÇÃO */}
        {!loading && totalPaginas > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap", gap: "10px" }}>
            <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>
              Mostrando {indexPrimeiroItem + 1} até {Math.min(indexUltimoItem, associadosOrdenados.length)} de {associadosOrdenados.length} registros
            </span>
            
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                disabled={paginaAtual === 1}
                style={{ padding: "8px 16px", background: "rgba(255,255,255,0.05)", border: "none", color: "#fff", borderRadius: "8px", cursor: paginaAtual === 1 ? "not-allowed" : "pointer", opacity: paginaAtual === 1 ? 0.3 : 1, transition: "0.2s" }}
              >
                Anterior
              </button>
              
              <div style={{ padding: "8px 16px", background: "var(--c-accent)", color: "#000", borderRadius: "8px", fontWeight: "bold", fontSize: "14px" }}>
                {paginaAtual} de {totalPaginas}
              </div>
              
              <button
                onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                disabled={paginaAtual === totalPaginas}
                style={{ padding: "8px 16px", background: "rgba(255,255,255,0.05)", border: "none", color: "#fff", borderRadius: "8px", cursor: paginaAtual === totalPaginas ? "not-allowed" : "pointer", opacity: paginaAtual === totalPaginas ? 0.3 : 1, transition: "0.2s" }}
              >
                Próxima
              </button>
            </div>
          </div>
        )}

      </div>

      {modalOpen && associadoSelecionado && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Detalhes do Associado</h3>
            <div className="modal-grid">
              <div className="modal-field"><strong>Vaqueiro</strong><span>{associadoSelecionado.nomeVaqueiro}</span></div>
              <div className="modal-field"><strong>Email</strong><span>{associadoSelecionado.userId?.email || "Não informado"}</span></div>
              <div className="modal-field"><strong>WhatsApp</strong><span>{associadoSelecionado.userId?.whatsapp || "Não informado"}</span></div>
              <div className="modal-field"><strong>CPF</strong><span>{associadoSelecionado.cpfVaqueiro || associadoSelecionado.userId?.cpf || "Não informado"}</span></div>
              <div className="modal-field"><strong>Equipe</strong><span>{associadoSelecionado.equipe}</span></div>
              <div className="modal-field"><strong>Categoria</strong><span>{associadoSelecionado.categoria}</span></div>
              <div className="modal-field"><strong>Cavalo Registrado</strong><span>{associadoSelecionado.cavalo || "Nenhum cavalo cadastrado"}</span></div>
              <div className="modal-field"><strong>Status de Pagamento</strong><span className={`status ${associadoSelecionado.statusPagamento}`} style={{ marginTop: "4px", width: "fit-content" }}>{associadoSelecionado.statusPagamento}</span></div>
              <div className="modal-field"><strong>Data de Inscrição</strong><span>{new Date(associadoSelecionado.createdAt).toLocaleString()}</span></div>
            </div>
            <button onClick={fecharModal} className="btn-fechar">Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
}